import React, { useState, useEffect } from 'react';
import { api } from '../api';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 9,
    color: '#3b82f6',
    icon: '🚀',
    features: [
      'Clients illimités',
      'Factures & devis PDF',
      'Numérotation automatique',
      'Profil entreprise',
      'Export CSV',
      'Support inclus',
    ],
  },
  {
    id: 'essentiel',
    name: 'Essentiel',
    price: 19,
    color: '#8b5cf6',
    icon: '⚡',
    popular: true,
    features: [
      'Tout du plan Starter',
      'Comptabilité (grand livre)',
      'Documents entrants',
      'Agent IA (questions & conseils)',
      'Notifications email automatiques',
      'Support prioritaire',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 39,
    color: '#f59e0b',
    icon: '👑',
    features: [
      'Tout du plan Essentiel',
      'Bilan comptable & graphiques',
      'Réseaux sociaux gérés par AKO',
      'Gestion de projets & propositions',
      'Rapports personnalisés',
      'Support dédié 7j/7',
    ],
  },
];

const s = {
  card: { background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '1rem', padding: '2rem' },
  btn: { background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.75rem 1.5rem', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', width: '100%', transition: 'all 0.2s' },
};

const Abonnement = ({ user }) => {
  const [currentPlan, setCurrentPlan] = useState(null);
  const [planSource, setPlanSource] = useState(null); // 'stripe' | 'code' | null
  const [activeCode, setActiveCode] = useState(null); // { code, plan, expires_at }
  const [loading, setLoading] = useState(null); // id du plan en cours de paiement
  const [portalLoading, setPortalLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [codeInput, setCodeInput] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);

  const loadPlan = async () => {
    try {
      const d = await api.getPlan();
      setCurrentPlan(d?.plan || null);
      setPlanSource(d?.source || null);
      if (d?.source === 'code') {
        const ac = await api.getActiveCode().catch(() => null);
        setActiveCode(ac?.code || null);
      }
    } catch {
      setCurrentPlan(null);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Retour Stripe
    if (params.get('success') === 'true') {
      const plan = params.get('plan');
      setMessage({ type: 'success', text: `Abonnement ${plan} activé ! Bienvenue 🎉` });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('cancelled') === 'true') {
      setMessage({ type: 'warning', text: 'Paiement annulé.' });
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Auto-activation via lien ?code=AKO-XXXX-XXXX
    const codeParam = params.get('code');
    if (codeParam) {
      setCodeInput(codeParam.toUpperCase());
      window.history.replaceState({}, '', window.location.pathname);
      // Auto-activer après chargement du plan
      loadPlan().finally(async () => {
        setPageLoading(false);
        setCodeLoading(true);
        try {
          const d = await api.activateCode(codeParam.toUpperCase());
          const planName = d.plan ? d.plan.charAt(0).toUpperCase() + d.plan.slice(1) : '';
          setMessage({ type: 'success', text: `🎉 Code activé ! Accès ${planName} valable 2 mois.` });
          setCodeInput('');
          await loadPlan();
        } catch (e) {
          setMessage({ type: 'error', text: e.message || 'Code invalide ou déjà utilisé' });
        }
        setCodeLoading(false);
      });
      return;
    }

    loadPlan().finally(() => setPageLoading(false));
  }, []); // eslint-disable-line

  const handleActivateCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    setCodeLoading(true);
    try {
      const d = await api.activateCode(code);
      setMessage({ type: 'success', text: `Code activé ! Accès ${d.plan || ''} valable 2 mois.` });
      setCodeInput('');
      await loadPlan();
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Code invalide ou déjà utilisé' });
    }
    setCodeLoading(false);
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const data = await api.openPortal();
      if (data.url) window.location.href = data.url;
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Erreur portail Stripe' });
    }
    setPortalLoading(false);
  };

  const handleSubscribe = async (planId) => {
    setLoading(planId);
    try {
      const data = await api.createCheckout(planId);
      if (data.url) window.location.href = data.url;
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Erreur lors de la redirection vers le paiement' });
    }
    setLoading(null);
  };

  if (pageLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
      <div style={{ width: '40px', height: '40px', border: '3px solid #334155', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: 'white', margin: '0 0 0.5rem' }}>💳 Abonnement</h2>
        <p style={{ color: '#64748b', margin: 0 }}>
          {currentPlan && currentPlan !== 'free'
            ? <>Plan actuel : <span style={{ color: '#34d399', fontWeight: '700' }}>{currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}</span>
                {planSource === 'code' && activeCode && (
                  <span style={{ marginLeft: '0.75rem', background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24', borderRadius: '999px', padding: '0.15rem 0.6rem', fontSize: '0.78rem', fontWeight: '700' }}>
                    Code d'essai — expire le {new Date(activeCode.expires_at).toLocaleDateString('fr-FR')}
                  </span>
                )}
              </>
            : 'Choisissez le plan adapté à votre activité'}
        </p>
      </div>

      {/* Bouton gestion abonnement */}
      {currentPlan && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <button onClick={handlePortal} disabled={portalLoading} style={{
            background: 'rgba(30,41,59,0.8)', border: '1px solid #475569', borderRadius: '0.5rem',
            padding: '0.65rem 1.25rem', color: '#94a3b8', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem',
            display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'all 0.2s',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#64748b'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#475569'; e.currentTarget.style.color = '#94a3b8'; }}>
            {portalLoading ? '⏳ Redirection...' : '⚙️ Gérer mon abonnement'}
          </button>
          <span style={{ color: '#64748b', fontSize: '0.8rem' }}>Modifier, annuler ou télécharger vos factures Stripe</span>
        </div>
      )}

      {/* Message retour Stripe */}
      {message && (
        <div style={{
          background: message.type === 'success' ? 'rgba(16,185,129,0.15)' : message.type === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(251,191,36,0.15)',
          border: `1px solid ${message.type === 'success' ? 'rgba(16,185,129,0.4)' : message.type === 'error' ? 'rgba(239,68,68,0.4)' : 'rgba(251,191,36,0.4)'}`,
          borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1.5rem',
          color: message.type === 'success' ? '#34d399' : message.type === 'error' ? '#fca5a5' : '#fbbf24',
          fontWeight: '600',
        }}>
          {message.type === 'success' ? '✅' : message.type === 'error' ? '❌' : '⚠️'} {message.text}
        </div>
      )}

      {/* 3 plans */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {PLANS.map(plan => {
          // 'essentiel' plan from API maps to 'starter' plan id or vice versa
          const isActive = currentPlan === plan.id || (plan.id === 'starter' && currentPlan === 'essentiel') || (plan.id === 'essentiel' && currentPlan === 'starter');
          const isLoading = loading === plan.id;
          return (
            <div key={plan.id} style={{
              ...s.card,
              borderColor: plan.popular ? plan.color : '#334155',
              borderWidth: plan.popular ? '2px' : '1px',
              position: 'relative',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>

              {plan.popular && (
                <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', background: `linear-gradient(135deg,${plan.color},#6d28d9)`, color: 'white', padding: '0.25rem 1rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '700', whiteSpace: 'nowrap' }}>
                  ⭐ Le plus populaire
                </div>
              )}

              {isActive && (
                <div style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.4)', color: '#34d399', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '700' }}>
                  ✓ Actif
                </div>
              )}

              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>{plan.icon}</div>
              <h3 style={{ color: 'white', margin: '0 0 0.25rem', fontSize: '1.4rem' }}>{plan.name}</h3>
              <div style={{ marginBottom: '1.5rem' }}>
                <span style={{ color: plan.color, fontSize: '2.5rem', fontWeight: '800' }}>{plan.price}€</span>
                <span style={{ color: '#64748b', fontSize: '0.9rem' }}> / mois</span>
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {plan.features.map((f, i) => (
                  <li key={i} style={{ color: '#cbd5e1', fontSize: '0.875rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <span style={{ color: plan.color, fontWeight: '700', flexShrink: 0, marginTop: '1px' }}>✓</span> {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => !isActive && handleSubscribe(plan.id)}
                disabled={isActive || isLoading}
                style={{
                  ...s.btn,
                  background: isActive ? 'rgba(16,185,129,0.2)' : `linear-gradient(135deg,${plan.color},${plan.color}cc)`,
                  color: isActive ? '#34d399' : 'white',
                  cursor: isActive ? 'default' : 'pointer',
                  opacity: isLoading ? 0.7 : 1,
                  boxShadow: isActive ? 'none' : `0 4px 14px ${plan.color}40`,
                }}>
                {isLoading ? '⏳ Redirection...' : isActive ? '✓ Plan actuel' : `Commencer — ${plan.price}€/mois`}
              </button>
            </div>
          );
        })}
      </div>

      {/* Code d'accès — toujours visible */}
      <div style={{ ...s.card, marginBottom: '1.5rem', borderColor: 'rgba(251,191,36,0.4)', background: 'linear-gradient(135deg,rgba(30,41,59,0.9),rgba(251,191,36,0.04))' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <span style={{ fontSize: '1.5rem' }}>🎟️</span>
          <div>
            <h3 style={{ color: 'white', margin: 0, fontSize: '1.1rem' }}>Code d'accès</h3>
            <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.85rem' }}>
              {planSource === 'code' && activeCode
                ? <>Code actif — Plan <strong style={{ color: '#fbbf24' }}>{activeCode.plan?.charAt(0).toUpperCase() + activeCode.plan?.slice(1)}</strong> · Expire le <strong style={{ color: 'white' }}>{new Date(activeCode.expires_at).toLocaleDateString('fr-FR')}</strong></>
                : 'Entrez votre code AKO pour activer un accès gratuit de 2 mois'
              }
            </p>
          </div>
        </div>

        {codeLoading && (
          <div style={{ color: '#fbbf24', fontSize: '0.875rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ display: 'inline-block', width: '14px', height: '14px', border: '2px solid #fbbf2440', borderTop: '2px solid #fbbf24', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            Activation en cours...
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <input
            value={codeInput}
            onChange={e => setCodeInput(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && handleActivateCode()}
            placeholder="AKO-XXXX-XXXX"
            maxLength={14}
            disabled={codeLoading}
            style={{ background: 'rgba(51,65,85,0.5)', border: '1px solid #475569', borderRadius: '0.5rem', color: 'white', fontWeight: '600', fontSize: '1rem', letterSpacing: '0.08em', flex: 1, minWidth: '180px', padding: '0.75rem 1rem', fontFamily: 'monospace', outline: 'none', cursor: 'text' }}
          />
          <button
            onClick={handleActivateCode}
            disabled={codeLoading || !codeInput.trim()}
            style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.75rem 1.5rem', cursor: codeLoading || !codeInput.trim() ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '1rem', opacity: codeLoading || !codeInput.trim() ? 0.6 : 1, whiteSpace: 'nowrap', boxShadow: '0 4px 14px rgba(245,158,11,0.3)' }}>
            {codeLoading ? '⏳ Activation...' : '🔓 Activer le code'}
          </button>
        </div>

        {/* Explication du format */}
        <p style={{ color: '#64748b', fontSize: '0.78rem', margin: '0.75rem 0 0', fontFamily: 'monospace', letterSpacing: '0.04em' }}>
          FORMAT : AKO-XXXX-XXXX · Le code vous a été communiqué par FacturArt
        </p>
      </div>

      {/* Plan Sur mesure */}
      <div style={{ ...s.card, marginBottom: '2rem', borderColor: 'rgba(236,72,153,0.4)', background: 'linear-gradient(135deg,rgba(30,41,59,0.9),rgba(236,72,153,0.05))' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1.5rem' }}>
          <div style={{ flex: 1, minWidth: '260px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '2rem' }}>🏗️</span>
              <h3 style={{ color: 'white', margin: 0, fontSize: '1.4rem' }}>Sur mesure</h3>
              <span style={{ background: 'rgba(236,72,153,0.15)', border: '1px solid rgba(236,72,153,0.3)', color: '#f472b6', borderRadius: '999px', padding: '0.15rem 0.75rem', fontSize: '0.75rem', fontWeight: '700' }}>Prix sur devis</span>
            </div>
            <p style={{ color: '#94a3b8', margin: '0 0 1rem', fontSize: '0.9rem', lineHeight: '1.7' }}>
              Vous souhaitez automatiser entièrement votre entreprise ou avoir votre propre système sur mesure ?
              On conçoit une solution adaptée à vos besoins : workflows n8n dédiés, intégrations API, automatisations métier, tableaux de bord personnalisés.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {['Système dédié', 'Workflows sur mesure', 'Intégrations API', 'Automatisation complète', 'Formation incluse'].map((t, i) => (
                <span key={i} style={{ background: 'rgba(236,72,153,0.1)', border: '1px solid rgba(236,72,153,0.25)', color: '#f472b6', borderRadius: '999px', padding: '0.2rem 0.75rem', fontSize: '0.8rem' }}>{t}</span>
              ))}
            </div>
          </div>
          <a href="mailto:komlan@akodigitale.fr?subject=Demande Sur Mesure FacturArt" style={{
            display: 'inline-block', background: 'linear-gradient(135deg,#ec4899,#be185d)', color: 'white',
            borderRadius: '0.5rem', padding: '0.85rem 1.75rem', fontWeight: '700', fontSize: '1rem',
            textDecoration: 'none', whiteSpace: 'nowrap', boxShadow: '0 4px 14px rgba(236,72,153,0.35)',
          }}>
            Nous contacter
          </a>
        </div>
      </div>

      {/* Garanties */}
      <div style={{ ...s.card, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '1.5rem', textAlign: 'center' }}>
        {[
          { icon: '🔒', title: 'Paiement sécurisé', desc: 'Stripe — certifié PCI DSS' },
          { icon: '🔄', title: 'Sans engagement', desc: 'Résiliez à tout moment' },
          { icon: '💬', title: 'Support inclus', desc: 'On est là pour vous aider' },
        ].map((g, i) => (
          <div key={i}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{g.icon}</div>
            <div style={{ color: 'white', fontWeight: '600', marginBottom: '0.25rem' }}>{g.title}</div>
            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>{g.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Abonnement;
